import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TopTabbar from "../components/partials/TopTabbarIndex";
import Header from "../components/partials/Header";
import LoadingSpinner from '../utils/LoadingSpinner'; // Assurez-vous que le chemin est correct

const PrivacyPolicyPage = () => {
    const [isPageLoading, setIsPageLoading] = useState(true);
    const observerRef = useRef(null);
    const sectionRefsRef = useRef([]);

    useEffect(() => {
        // Simuler un délai de chargement
        const timer = setTimeout(() => {
            setIsPageLoading(false);
        }, 2000); // Ajustez le délai selon vos besoins

        const animatedSections = document.querySelectorAll('.ip-animated-section');
        sectionRefsRef.current = Array.from(animatedSections);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                } else {
                    entry.target.classList.remove('is-visible');
                }
            });
        }, observerOptions);

        sectionRefsRef.current.forEach(section => {
            if (section) {
                observerRef.current.observe(section);
            }
        });

        return () => {
            if (observerRef.current) {
                sectionRefsRef.current.forEach(section => {
                    if (section) {
                        observerRef.current.unobserve(section);
                    }
                });
            }
            clearTimeout(timer);
        };
    }, []);

    // Styles CSS intégrés
    const styles = `
        :root {
            --primary-color: #24afcc;
            --secondary-color: #29649c;
            --accent-color: #ffd700;
            --dark-color: #1a2a3a;
            --light-color: #f8f9fa;
            --gradient-primary: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
            --gradient-accent: linear-gradient(90deg, var(--accent-color), var(--primary-color));
        }

        .full-width-container {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
        }

        .hero-section {
            background: var(--gradient-primary);
            color: white;
            padding: 6rem 0;
            position: relative;
            overflow: hidden;
        }

        .hero-content {
            position: relative;
            z-index: 2;
        }

        .hero-title {
            font-size: 3rem;
            font-weight: 800;
            margin-bottom: 1.5rem;
            text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .hero-description {
            font-size: 1.25rem;
            margin-bottom: 2rem;
            opacity: 0.9;
        }

        .policy-content {
            width: 100%;
            padding: 3rem 0;
            background: white;
        }

        .policy-block {
            width: 100%;
            padding: 2rem 0;
            margin-bottom: 2rem;
        }

        .policy-container {
            background: white;
            border-radius: 12px;
            padding: 3rem;
            box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }

        .policy-container h2 {
            color: var(--secondary-color);
            border-bottom: 2px solid var(--primary-color);
            padding-bottom: 1rem;
            margin-bottom: 2rem;
        }

        .policy-container h3 {
            color: var(--primary-color);
            margin: 2rem 0 1rem;
        }

        .policy-container p,
        .policy-container li {
            color: #555;
            line-height: 1.7;
            margin-bottom: 1rem;
        }

        .policy-container ul {
            padding-left: 1.5rem;
        }

        .policy-container a {
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 500;
        }

        .policy-container a:hover {
            text-decoration: underline;
        }

        .contact-section-with-bg {
            position: relative;
            background: url(assets/images/bg/6.jpg) no-repeat center center;
            background-size: cover;
            z-index: 1;
        }

        .contact-section-with-bg::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.6);
            z-index: -1;
        }

        .footer {
            background: var(--dark-color);
            color: white;
            padding: 4rem 0 2rem;
        }

        .footer-links a {
            color: rgba(255,255,255,0.7);
            transition: color 0.3s ease;
        }

        .footer-links a:hover {
            color: white;
            text-decoration: none;
        }

        @media (max-width: 768px) {
            .hero-title {
                font-size: 2.25rem;
            }

            .hero-description {
                font-size: 1rem;
            }

            .policy-container {
                padding: 2rem 1.5rem;
            }
        }
    `;

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement de la politique de confidentialité..." />;
    }

    return (
        <div className="ip-scroll-container full-width-container">
            <style>{styles}</style>
            <TopTabbar />
            <Header />
            {/* Section Hero */}
            <section className="hero-section ip-animated-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-12 text-center hero-content">
                            <h1 className="hero-title">Politique de Confidentialité</h1>
                            <p className="hero-description">Comment nous protégeons et utilisons vos informations chez NTR Finance</p>
                        </div>
                    </div>
                </div>
            </section>
            {/* Contenu Politique */}
            <div className="policy-content">
                <div className="container">
                    <div className="policy-block ip-animated-section">
                        <div className="policy-container">
                            <h2>Politique de Confidentialité de NTR Finance</h2>
                            <p>
                                Chez NTR Finance, nous nous engageons à protéger la confidentialité et la sécurité de vos données personnelles. Cette politique explique comment nous collectons, utilisons, partageons et protégeons vos informations lorsque vous utilisez nos services.
                            </p>
                            <h3>1. Collecte des Données Personnelles</h3>
                            <p>
                                Nous collectons les données personnelles suivantes :
                            </p>
                            <ul>
                                <li className="policy-ul"><strong>Informations d'identification :</strong> Nom, adresse, date de naissance, numéro de téléphone, email</li>
                                <li className="policy-ul"><strong>Informations financières :</strong> Détails de paiement, informations bancaires</li>
                                <li className="policy-ul"><strong>Informations de transaction :</strong> Historique des transferts, montants, bénéficiaires</li>
                                <li className="policy-ul"><strong>Données techniques :</strong> Adresse IP, type de navigateur, données d'utilisation</li>
                            </ul>
                            <h3>2. Utilisation de Vos Données</h3>
                            <p>
                                Vos données sont utilisées pour :
                            </p>
                            <ul>
                                <li className="policy-ul">Fournir et améliorer nos services de transfert d'argent</li>
                                <li className="policy-ul">Vérifier votre identité et prévenir la fraude</li>
                                <li className="policy-ul">Vous informer des mises à jour et offres</li>
                                <li className="policy-ul">Se conformer aux obligations légales</li>
                            </ul>
                            <h3>3. Partage des Données</h3>
                            <p>
                                Nous pouvons partager vos données avec :
                            </p>
                            <ul>
                                <li className="policy-ul">Partenaires financiers pour traiter les transactions</li>
                                <li className="policy-ul">Autorités réglementaires lorsque requis par la loi</li>
                                <li className="policy-ul">Fournisseurs de services qui nous aident à opérer</li>
                            </ul>
                            <h3>4. Sécurité des Données</h3>
                            <p>
                                Nous utilisons des mesures de sécurité avancées incluant :
                            </p>
                            <ul>
                                <li className="policy-ul">Cryptage AES-256 pour toutes les données</li>
                                <li className="policy-ul">Contrôles d'accès strict</li>
                                <li className="policy-ul">Audits de sécurité réguliers</li>
                            </ul>
                            <h3>5. Conservation des Données</h3>
                            <p>
                                Nous conservons vos données aussi longtemps que nécessaire pour :
                            </p>
                            <ul>
                                <li className="policy-ul">Fournir nos services</li>
                                <li className="policy-ul">Remplir nos obligations légales</li>
                                <li className="policy-ul">Résoudre les litiges</li>
                            </ul>
                            <h3>6. Vos Droits</h3>
                            <p>
                                Vous avez le droit de :
                            </p>
                            <ul>
                                <li className="policy-ul">Accéder à vos données personnelles</li>
                                <li className="policy-ul">Demander la correction ou la suppression</li>
                                <li className="policy-ul">Vous opposer au traitement</li>
                            </ul>
                            <h3>7. Modifications</h3>
                            <p>
                                Nous pouvons mettre à jour cette politique. Les changements seront publiés sur cette page avec une date de révision.
                            </p>
                            <h3>8. Nous Contacter</h3>
                            <p>
                                Pour toute question concernant cette politique ou vos données :
                            </p>
                            <ul>
                                <li className="policy-ul">Email : <a href="mailto:ntrfinancial@gmail.com">ntrfinancial@gmail.com</a></li>
                                <li className="policy-ul">Téléphone : <a href="tel:+15879894933">+1 587-989-4933</a></li>
                                <li className="policy-ul">Adresse : 5374 rue bossuet, Lévis, Québec</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>


            {/* Section Contact */}
            <section className="contact-section-with-bg py-6 ip-animated-section text-white">
                <div className="container">
                    <div className="text-center mb-5 pt-5 pb-3">
                        <h2 className="display-5 fw-bold">Besoin d'aide ?</h2>
                        <p className="lead opacity-75">Notre équipe est disponible 24/7 pour répondre à vos
                            questions</p>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="row g-4">
                                {[
                                    {
                                        icon: "assets/svg/whatsapp-icon.svg",
                                        title: "WhatsApp",
                                        link: "https://wa.me/+15879894933",
                                        value: "+1 587-989-4933"
                                    },
                                    {
                                        icon: "assets/svg/mail-icon.svg",
                                        title: "Email",
                                        link: "mailto:support@ntrfinancial.com",
                                        value: "support@ntrfinancial.com"
                                    },
                                    {
                                        icon: "assets/svg/phone-icon.svg",
                                        title: "Téléphone",
                                        link: "tel:+15879894933",
                                        value: "+1 587-989-4933"
                                    },
                                    {
                                        icon: "assets/svg/facebook-icon.svg",
                                        title: "Facebook",
                                        link: "https://www.facebook.com/profile.php?id=61563752121174",
                                        value: "NTR Finance"
                                    }
                                ].map((contact, index) => (
                                    <div key={index} className="col-md-6">
                                        <a href={contact.link} target="_blank" rel="noopener noreferrer"
                                           className="text-white text-decoration-none">
                                            <div className="bg-white-10 rounded-lg p-4 h-100 d-flex align-items-center">
                                                <img src={contact.icon} alt={contact.title} style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'contain',
                                                    marginRight: '1rem'
                                                }}/>
                                                <div>
                                                    <h3 className="h5 mb-1">{contact.title}</h3>
                                                    <p className="mb-0 opacity-75">{contact.value}</p>
                                                </div>
                                            </div>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pied de Page */}
            <footer className="footer ip-animated-section">
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">NTR Finance</h3>
                            <p className="text-white-50">La solution simple et sécurisée pour vos transferts d'argent
                                vers l'Afrique.</p>
                        </div>
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">Liens rapides</h3>
                            <ul className="list-unstyled footer-links">
                                <li className="mb-2"><Link to="/" className="text-white-50">Accueil</Link></li>
                                <li className="mb-2"><Link to="/login" className="text-white-50">Connexion</Link></li>
                                <li className="mb-2"><Link to="/register" className="text-white-50">Inscription</Link>
                                </li>
                                <li><Link to="/privacy_policy" className="text-white-50">Politique de
                                    confidentialité</Link>
                                </li>

                                <li><Link to="/account-deletion-request" className="text-white-50">
                                Suppression de Compte
                                </Link></li>
                            </ul>
                        </div>
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">Contact</h3>
                            <ul className="list-unstyled text-white-50">
                                <li className="mb-2">5374 rue bossuet, Lévis, Québec</li>
                                <li className="mb-2">support@ntrfinancial.com</li>
                                <li className="mb-2">admin@ntrfinancial.com</li>
                                <li>+1 587-989-4933</li>
                                <h3 className="h5 text-white mt-4">Permis D’ESM du Québec</h3>
                                <li className="mb-2">Numéro : 0000000000018805</li>
                            </ul>
                        </div>
                    </div>
                    <hr className="my-5 border-white-10"/>
                    <div className="text-center">
                        <p className="mb-0 text-white-50">&copy; {new Date().getFullYear()} NTR Finance. Tous droits
                            réservés.</p>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default PrivacyPolicyPage;
